import { differenceInCalendarDays, format } from 'date-fns';
import { fromDateStampObject, timestampToUTCDatestampObject, toDate } from './translator';
export const timeTextsPath = 'common.time';
const INVALID_DATE_TEXT = 'Invalid Date';
const formatNumericUsDate = (date)=>Number.isNaN(date.getTime()) ? INVALID_DATE_TEXT : "".concat(date.getMonth() + 1, "/").concat(date.getDate(), "/").concat(date.getFullYear());
const formatWith = (formatter, date)=>Number.isNaN(date.getTime()) ? INVALID_DATE_TEXT : formatter.format(date);
const usDateWithTimeFormat = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric'
});
const usDateWithTimeAndTimezoneFormat = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    timeZoneName: 'short'
});
const usTimeFormat = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: 'numeric'
});
const usDateWithTimeShortMonthFormat = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true
});
const usDateWithTimeShortMonthAndTimezoneFormat = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
    timeZoneName: 'short'
});
const toCalendarDate = (datestamp)=>typeof datestamp === 'number' || typeof datestamp === 'string' ? fromDateStampObject(timestampToUTCDatestampObject(new Date(datestamp).getTime())) : datestamp instanceof Date ? datestamp : toDate(datestamp);
export function formatDate(datestamp) {
    let { long } = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
    const date = toCalendarDate(datestamp);
    return long ? format(date, 'PP') : formatNumericUsDate(date);
}
export function formatDateLong(datestamp) {
    return formatDate(datestamp, {
        long: true
    });
}
export function formatDateLongOrdinal(datestamp) {
    const date = toCalendarDate(datestamp);
    return format(date, 'MMMM do, y');
}
export function formatMonthYear(datestamp) {
    return format(toCalendarDate(datestamp), 'MMMM yyyy');
}
export function formatDateWithTime(datestamp) {
    let withTimezone = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : false;
    const date = datestamp instanceof Date ? datestamp : toDate(datestamp);
    return formatWith(withTimezone ? usDateWithTimeAndTimezoneFormat : usDateWithTimeFormat, date);
}
export function formatTime(datestamp) {
    const date = datestamp instanceof Date ? datestamp : toDate(datestamp);
    return formatWith(usTimeFormat, date);
}
export function formatDateWithTimeShortMonth(datestamp) {
    let withTimezone = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : false;
    const date = datestamp instanceof Date ? datestamp : toDate(datestamp);
    return formatWith(withTimezone ? usDateWithTimeShortMonthAndTimezoneFormat : usDateWithTimeShortMonthFormat, date);
}
export function formatDateTimeWithZone(dateTime, locale, timeZone) {
    const date = new Date(dateTime);
    if (Number.isNaN(date.getTime())) return null;
    const day = new Intl.DateTimeFormat(locale, {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone
    }).format(date);
    const time = new Intl.DateTimeFormat(locale, {
        hour: 'numeric',
        minute: '2-digit',
        timeZone
    }).format(date);
    const zone = new Intl.DateTimeFormat(locale, {
        timeZoneName: 'short',
        timeZone
    }).formatToParts(date).filter((part)=>part.type === 'timeZoneName').map((part)=>part.value).join('');
    return "".concat(day, " \xb7 ").concat(time, " (").concat(zone, ")");
}
const relativeTimeFormatter = new Intl.RelativeTimeFormat('en', {
    numeric: 'auto'
});
export function formatDaysUntil(timestamp) {
    let now = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : new Date();
    const diff = timestamp > now.getTime() ? differenceInCalendarDays(timestamp, now) : 0;
    return relativeTimeFormatter.format(diff, 'day');
}
const twoMinutes = 1000 * 60 * 2;
const oneHourMs = 1000 * 60 * 60;
const oneDayMs = oneHourMs * 24;
const twoWeeks = oneDayMs * 14;
export function formatTimeAgo(timestamp, texts, now) {
    const timePassed = now - timestamp;
    if (timePassed <= twoMinutes) {
        return texts.justNow();
    } else if (timePassed >= twoWeeks) {
        // WHY: past the relative window the label uses the long date the tables use, not the numeric form.
        return formatDate(timestamp, {
            long: true
        });
    } else if (timePassed < oneHourMs) {
        const count = Math.floor(timePassed / (1000 * 60));
        return texts.minutesAgo({
            count
        });
    } else if (timePassed < oneDayMs) {
        const count = Math.round(timePassed / oneHourMs);
        return texts.hoursAgo({
            count
        });
    } else {
        const count = Math.floor(timePassed / oneDayMs);
        return texts.daysAgo({
            count
        });
    }
}
export const formatDuration = (param)=>{
    let { hours, minutes, texts } = param;
    return texts.duration({
        hours: hours !== null && hours !== void 0 ? hours : 0,
        minutes
    });
};
